import { jsonDeserialize, JsonReviver } from '@palador/json';
import { ErrorStore } from '../data-stores';
import { ErrorContext } from './error-context';

export interface StatusResponse {
  statusCode: number;
  message: string;
}

export const responseErrorHandler = async <T>(
  errorStore: ErrorStore,
  response: Response,
  operationTitle: string,
  jsonContentExpected = true,
  statusesToIgnoreError: number[] = [],
  badRequestInJsonExpected = false,
  revivers?: JsonReviver[],
  errorContext?: ErrorContext,
): Promise<T> => {
  const { status, statusText } = response;
  const success = (status >= 200 && status < 300);
  const errorIgnored = statusesToIgnoreError.includes(status);
  const statusResponse: StatusResponse = { statusCode: status, message: statusText };
  if (status === 401 && !statusResponse.message) {
    statusResponse.message = await response.text();
  }

  if (status === 204) return Promise.resolve<T>(undefined!);

  const parseJSON = async (response: Response): Promise<T> => {
    const text = await response.text();
    return jsonDeserialize<T>(text, revivers);
  };

  if (success && jsonContentExpected) {
    const text = await response.text();
    if (text.trim() === '') {
      return undefined as T;
    }
    return jsonDeserialize<T>(text, revivers);
  }

  if (success && !jsonContentExpected) {
    return Promise.resolve<T>(response.text() as unknown as T);
  }

  if (!success && !jsonContentExpected) {
    return Promise.resolve<T>(response.text() as unknown as T);
  }

  if (!success && status === 400 && badRequestInJsonExpected) {
    const responseError = await parseJSON(response);

    if (Array.isArray(responseError) && errorContext?.fieldMappings) {
      const transformedErrors = responseError.map((error) => {
        if (typeof error === 'string') {
          const matchedField = Object.keys(errorContext.fieldMappings).find((fieldKey) => error.toLowerCase().includes(fieldKey.toLowerCase()));

          if (matchedField) {
            const friendlyFieldName = errorContext.fieldMappings[matchedField];
            return error.replace(new RegExp(matchedField, 'gi'), friendlyFieldName);
          }
        }
        return error;
      });

      errorStore.reportValidationErrors(transformedErrors);
      return Promise.reject(transformedErrors);
    }

    if (Array.isArray(responseError) && !errorContext) {
      errorStore.reportValidationErrors(responseError);
    }

    return Promise.reject(responseError);
  }

  if (!success && errorIgnored) {
    return Promise.resolve<T>(undefined!);
  }

  if (!success && status === 400 && !badRequestInJsonExpected) {
    statusResponse.message = await response.text();
  }

  return Promise.reject(statusResponse);
};
